import React, { useState, useEffect, KeyboardEvent } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Tooltip,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { formatDistanceToNow } from "date-fns/formatDistanceToNow";
import { useAppSelector, useAppDispatch } from "@store/hooks";
import {
  closeResourceManagementModal,
  setBaseView,
} from "@store/slices/viewSlice";
import { selectCanvas } from "@store/thunks/workspaceThunks";
import {
  loadAllWorkspaceCanvases,
  renameCanvas,
  deleteCanvas,
  selectAllWorkspaceCanvases,
  selectWorkspaceCanvasesLoading,
  selectWorkspaceCanvasesError,
} from "@features/workspace-canvases/state/workspaceCanvasesSlice";
import { selectIsEvaluator } from "@store/selectors";
import EmptyState from "@shared/components/states/EmptyState";
import { CreateCanvasModal } from "./CreateCanvasModal";
import type { RootState } from "@store/index";

export const CanvasesTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const canvases = useAppSelector(selectAllWorkspaceCanvases);
  const loading = useAppSelector(selectWorkspaceCanvasesLoading);
  const error = useAppSelector(selectWorkspaceCanvasesError);
  const isEvaluator = useAppSelector(selectIsEvaluator);
  const workspaceId = useAppSelector(
    (state: RootState) => state.workspace.workspace_id
  );

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (workspaceId) {
      dispatch(loadAllWorkspaceCanvases(workspaceId));
    }
  }, [workspaceId, dispatch]);

  const handleView = (canvasId: string) => {
    dispatch(setBaseView("canvas"));
    dispatch(selectCanvas(canvasId));
    dispatch(closeResourceManagementModal());
  };

  const startEdit = (canvasId: string, currentName: string) => {
    setEditingId(canvasId);
    setEditValue(currentName);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const commitEdit = async (canvasId: string) => {
    const next = editValue.trim();
    const current = canvases.find((c) => c.canvas_id === canvasId);
    if (!current || !next || next === current.canvas_name) {
      cancelEdit();
      return;
    }
    setSavingId(canvasId);
    try {
      await dispatch(renameCanvas({ canvasId, canvasName: next })).unwrap();
    } catch (err) {
      console.error("Failed to rename canvas:", err);
      alert("Failed to rename canvas. Please try again.");
    } finally {
      setSavingId(null);
      cancelEdit();
    }
  };

  const handleEditKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    canvasId: string
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEdit(canvasId);
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  };

  const handleDelete = async (canvasId: string) => {
    const canvas = canvases.find((c) => c.canvas_id === canvasId);
    if (!canvas) return;
    if (
      !window.confirm(
        `Delete "${canvas.canvas_name || "Untitled Canvas"}"? This action cannot be undone.`
      )
    ) {
      return;
    }
    setDeletingId(canvasId);
    try {
      await dispatch(deleteCanvas(canvasId)).unwrap();
    } catch (err) {
      console.error("Failed to delete canvas:", err);
      alert("Failed to delete canvas. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading && canvases.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (canvases.length === 0) {
    return (
      <>
        <EmptyState
          icon={
            <AccountTreeOutlinedIcon
              sx={{ fontSize: 64, color: "text.secondary" }}
            />
          }
          title="No canvases yet"
          description="Create argument diagrams to visualize reasoning. Start by adding your first canvas."
          actionLabel="Create Canvas"
          onAction={() => setCreateModalOpen(true)}
        />
        <CreateCanvasModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          workspaceId={workspaceId}
        />
      </>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Workspace Canvases</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
        >
          Create Canvas
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Created</TableCell>
              {isEvaluator && (
                <>
                  <TableCell>Assigned By</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Required</TableCell>
                </>
              )}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {canvases.map((canvas) => {
              const isEditing = editingId === canvas.canvas_id;
              const isSaving = savingId === canvas.canvas_id;
              const isDeleting = deletingId === canvas.canvas_id;
              const isOverdue =
                canvas.due_date &&
                new Date(canvas.due_date) < new Date();
              return (
                <TableRow key={canvas.canvas_id} hover>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => handleEditKeyDown(e, canvas.canvas_id)}
                        onBlur={() => commitEdit(canvas.canvas_id)}
                        autoFocus
                        disabled={isSaving}
                        inputProps={{ maxLength: 255 }}
                        fullWidth
                      />
                    ) : (
                      <>
                        <Typography variant="body1" fontWeight={500}>
                          {canvas.canvas_name || "Untitled Canvas"}
                        </Typography>
                        {canvas.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                          >
                            {canvas.description}
                          </Typography>
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(canvas.created_at)}
                    </Typography>
                  </TableCell>
                  {isEvaluator && (
                    <>
                      <TableCell>
                        {canvas.assigned_by_username || "—"}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={isOverdue ? "error" : "text.primary"}
                        >
                          {canvas.due_date ? formatDate(canvas.due_date) : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={canvas.required ? "Required" : "Optional"}
                          size="small"
                          color={canvas.required ? "error" : "default"}
                          variant={canvas.required ? "filled" : "outlined"}
                        />
                      </TableCell>
                    </>
                  )}
                  <TableCell align="right">
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Tooltip title="Open canvas">
                        <span>
                          <IconButton
                            size="small"
                            color="inherit"
                            onClick={() => handleView(canvas.canvas_id)}
                            disabled={isEditing}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Rename">
                        <span>
                          <IconButton
                            size="small"
                            color="inherit"
                            onClick={() =>
                              startEdit(canvas.canvas_id, canvas.canvas_name || "")
                            }
                            disabled={isEditing}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(canvas.canvas_id)}
                            disabled={isDeleting || isEditing}
                          >
                            {isDeleting ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <CreateCanvasModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        workspaceId={workspaceId}
      />
    </Box>
  );
};
